import React, { useState, useEffect } from 'react'
import Typography from 'aldoo-ra/Typography'
import Button from 'aldoo-ra/Button'
import Input from 'aldoo-ra/Input'
import { GetCMSContent } from 'aldoo-ra/CMS/api'
import { showNotification } from 'aldoo-ra/Notification'

/**
 * Custom editor for AldooLayoutSlot data field
 * Shows component input parameters and allows editing slot data
 */
const SlotDataEditor = ({ slot, onSave, onClose }) => {
  const [componentInfo, setComponentInfo] = useState(null)
  const [slotData, setSlotData] = useState({})
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    loadComponentInfo()
  }, [slot])

  const loadComponentInfo = async () => {
    setIsLoading(true)
    try {
      // Get component reference from slot
      const componentRef = slot?.content?.component?.[0]
      if (!componentRef || !componentRef._id) {
        setIsLoading(false)
        return
      }

      // Fetch full component data to get input schema
      const response = await GetCMSContent({
        match: {
          _id: componentRef._id,
          'content.type': 'AldooComponent'
        },
        itemsPerPage: 1
      })

      if (response.result && response.data && response.data.length > 0) {
        const component = response.data[0]
        setComponentInfo(component.content)

        // Initialize slot data from existing slot or empty object
        setSlotData(slot?.content?.data || {})
      }
    } catch (error) {
      console.error('Failed to load component info:', error)
      showNotification({
        message: 'Failed to load component information',
        className: 'bg-admin_error text-white'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleFieldChange = (key, value) => {
    setSlotData(prev => ({
      ...prev,
      [key]: value
    }))
  }

  const handleSave = () => {
    // Update the slot's data field and call parent onSave
    const updatedSlot = {
      ...slot,
      content: {
        ...slot.content,
        data: slotData
      }
    }

    onSave(updatedSlot)
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <Typography variant="admin_txt1">Loading component information...</Typography>
      </div>
    )
  }

  if (!componentInfo) {
    return (
      <div className="p-6">
        <Typography variant="admin_txt1" className="text-admin_error">
          No component selected for this slot
        </Typography>
        <div className="mt-4 flex gap-2">
          <Button variant="admin_secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    )
  }

  // Get input schema from component
  const inputSchema = componentInfo.input || {}
  const inputKeys = Object.keys(inputSchema)

  return (
    <div className="p-6 space-y-6">
      {/* Data Editor */}
      <div className="space-y-4">
        {inputKeys.map(key => {
          const fieldDef = inputSchema[key]
          const value = slotData[key] || ''

          return (
            <div key={key}>
              <Typography variant="admin_label" className="mb-2 text-admin_text">
                {fieldDef.label || key}
                {fieldDef.required && <span className="text-admin_error ml-1">*</span>}
              </Typography>
              {fieldDef.description && (
                <Typography variant="admin_txt1_dim" className="text-xs mb-2">
                  {fieldDef.description}
                </Typography>
              )}
              <Input
                variant="admin"
                value={value}
                onChange={(e) => handleFieldChange(key, e.target.value)}
                placeholder={fieldDef.placeholder || `Enter ${fieldDef.label || key}`}
              />
            </div>
          )
        })}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end pt-4 border-t border-admin_bordered">
        <Button variant="admin_primary" onClick={handleSave}>
          Save
        </Button>
      </div>
    </div>
  )
}

export default SlotDataEditor
